import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { FolderKanban } from 'lucide-react';
import { Badge } from '../../components/Badge.tsx';
import { Banner } from '../../components/Banner.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { formatDate, shortId } from '../../components/format.ts';
import { StateLabel } from '../../components/TruthLabel.tsx';
import { EmptyState } from '../../components/EmptyState.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { readOwner } from '../../features/authorization/model.ts';
import {
  RUN_STATUS_LABEL,
  runStatusShape,
  runStatusTone,
  stepCounts,
} from '../../features/engagement/model.ts';
import type { RunView } from '../../features/engagement/types.ts';
import { api } from '../../features/onboarding/api.ts';
import { useWorkspaceFindings } from '../../features/findings/store.ts';
import { useSession } from '../../state/session.ts';
import styles from './WorkspacePages.module.css';

export function EngagementsPage() {
  const workspace = useSession((s) => s.workspace);
  const {
    findings,
    findingEngagementLinks,
    loaded,
    loading,
    stale,
    error,
    loadLatestAttemptFindings,
  } = useWorkspaceFindings();
  const ownerToken = workspace?.authorizationId ? readOwner(workspace.authorizationId) : '';
  const credentials = useMemo(
    () => ({ session: workspace?.setupSessionId, token: ownerToken }),
    [workspace?.setupSessionId, ownerToken],
  );

  useEffect(() => {
    if (workspace) {
      void loadLatestAttemptFindings(workspace.id, credentials);
    }
  }, [workspace, credentials, loadLatestAttemptFindings]);

  const links = findingEngagementLinks.filter((link) => link.workspaceId === workspace?.id);

  // Each row's run state comes from its own run view; null means the backend has no run.
  // shortcut: one request per engagement, fine for a handful; add a list endpoint if this grows.
  const [runs, setRuns] = useState<Record<string, RunView | null>>({});
  const engagementIds = links.map((link) => link.engagementId).join(',');
  useEffect(() => {
    let cancelled = false;
    for (const id of engagementIds ? engagementIds.split(',') : []) {
      api.engagementRun(id).then(
        (run) => !cancelled && setRuns((current) => ({ ...current, [id]: run })),
        () => !cancelled && setRuns((current) => ({ ...current, [id]: null })),
      );
    }
    return () => {
      cancelled = true;
    };
  }, [engagementIds]);

  return (
    <div className={styles.workspacePage}>
      <header className={styles.headingRow}>
        <div className={styles.header}>
          <h1 className={styles.title}>Engagements</h1>
        </div>
        <Link to="/findings" className={buttonClass('secondary', 'small')}>
          View findings
        </Link>
      </header>
      {error && (
        <Banner
          tone={stale ? 'attention' : 'negative'}
          title={stale ? 'Engagement links may be stale' : 'Could not load engagements'}
        >
          {error.message}
        </Banner>
      )}
      {loading && !loaded ? (
        <Skeleton label="Loading engagements…" />
      ) : error && !loaded ? null : links.length === 0 ? (
        <EmptyState
          title="No engagements yet"
          description="Every engagement starts from a finding. Open Findings and choose Create engagement on the one to investigate."
          variant="not-started"
          icon={FolderKanban}
        />
      ) : (
        <div className={styles.ledger}>
          <table className={styles.register} aria-label="Engagements">
            <thead>
              <tr>
                <th scope="col">Severity</th>
                <th scope="col">Investigation</th>
                <th scope="col">Status</th>
                <th scope="col">Steps</th>
                <th scope="col">Scope</th>
                <th scope="col">Opened</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => {
                const finding = findings.find((item) => item.findingId === link.findingId);
                const run = runs[link.engagementId];
                const counts = run ? stepCounts(run.steps) : null;
                return (
                  <tr key={link.linkId}>
                    <td data-label="Severity">
                      {finding && (
                        <StateLabel kind="severity" state={finding.normalizedSeverity} compact />
                      )}
                    </td>
                    <td data-label="Investigation">
                      <Link
                        to="/engagements/$engagementId"
                        params={{ engagementId: link.engagementId }}
                        className={styles.recordLink}
                      >
                        {finding?.title ?? 'Finding investigation'}
                      </Link>
                      <p className={styles.recordMeta}>
                        From finding{' '}
                        <Link
                          to="/findings/$findingId"
                          params={{ findingId: link.findingId }}
                          title={link.findingId}
                        >
                          {shortId(link.findingId)}
                        </Link>
                      </p>
                    </td>
                    <td data-label="Status">
                      {run ? (
                        <Badge
                          tone={runStatusTone(run.status)}
                          shape={runStatusShape(run.status)}
                          data-badge={run.status}
                        >
                          {RUN_STATUS_LABEL[run.status]}
                        </Badge>
                      ) : (
                        <span className={styles.recordMeta}>
                          {run === null ? 'No run yet' : 'Loading…'}
                        </span>
                      )}
                    </td>
                    <td data-label="Steps">
                      {counts?.total ? `${counts.finished} of ${counts.total} finished` : '—'}
                    </td>
                    <td data-label="Scope">
                      <code>{link.derivedScopeDomains.join(', ')}</code>
                    </td>
                    <td data-label="Opened">
                      <time dateTime={link.requestedAt}>{formatDate(link.requestedAt)}</time>
                      {' · '}
                      {link.requestedBy.displayName}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
